import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import * as messageService from '../../services/messageService';
import { API_BASE_URL } from '../../config/api';
import './MobileChatPage.css';

// ── Helpers ────────────────────────────────────────────────────────────────
const getAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('http')) return avatar;
  if (avatar.startsWith('/uploads')) return avatar;
  return `${API_BASE_URL}${avatar}`;
};

const getAttachmentUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) return url;
  return `${API_BASE_URL}${url}`;
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

// ── Tick SVGs ──────────────────────────────────────────────────────────────
const TickIcon = ({ status }) => {
  if (status === 'sending') {
    // Single gray tick
    return (
      <span className="mc-ticks mc-ticks-sent">
        <svg viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 1.5L6 10.5L1 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </span>
    );
  }
  if (status === 'read') {
    // Double blue tick
    return (
      <span className="mc-ticks mc-ticks-read">
        <svg viewBox="0 0 18 11" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 1L9 9L5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12.5 1L4.5 9L1 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </span>
    );
  }
  // Double gray tick (delivered / default)
  return (
    <span className="mc-ticks mc-ticks-delivered">
      <svg viewBox="0 0 18 11" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 1L9 9L5.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12.5 1L4.5 9L1 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  );
};

// ── Component ──────────────────────────────────────────────────────────────
const MobileChatPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { joinConversation, leaveConversation, on, off } = useSocket();

  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [imagePreview, setImagePreview] = useState(null); // { file, url, name }

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachMenuRef = useRef(null);

  const convId = parseInt(chatId, 10);

  // ── Fetch messages ─────────────────────────────────────────────────────
  const fetchMessages = useCallback(async (silent = false) => {
    if (!convId || !user?.id) return;
    try {
      if (!silent) setLoading(true);
      const data = await messageService.getMessages(convId, user.id);
      if (data?.messages) {
        setMessages(data.messages);
        if (data.sender) setOtherUser(data.sender);
      }
    } catch (_) {}
    finally { if (!silent) setLoading(false); }
  }, [convId, user?.id]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // ── Socket real-time ───────────────────────────────────────────────────
  useEffect(() => {
    if (!on || !off || !convId) return;
    joinConversation?.(convId);
    const handleNewMessage = ({ conversationId, message }) => {
      if (conversationId === convId) {
        setMessages(prev => prev.find(m => m.id === message.id) ? prev : [...prev, message]);
        messageService.markReadStatus(convId).catch(() => {});
      }
    };
    on('new_message', handleNewMessage);
    return () => { off('new_message', handleNewMessage); leaveConversation?.(convId); };
  }, [on, off, convId, joinConversation, leaveConversation]);

  // ── Auto-scroll ────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: messages.length <= 1 ? 'auto' : 'smooth' });
  }, [messages]);

  // ── Close attach menu on outside click ────────────────────────────────
  useEffect(() => {
    if (!showAttachMenu) return;
    const handler = (e) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
        setShowAttachMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAttachMenu]);

  // ── Send text message ─────────────────────────────────────────────────
  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || !user?.id || sending) return;
    setSending(true);
    const optimistic = {
      id: `tmp-${Date.now()}`,
      senderId: user.id,
      content: text,
      type: 'text',
      createdAt: new Date().toISOString(),
      isOwn: true,
    };
    setMessages(prev => [...prev, optimistic]);
    setInputText('');
    try {
      const sent = await messageService.sendMessage(convId, user.id, text);
      setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...sent.message, isOwn: true } : m));
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setInputText(text);
      if (err.code === 'MESSAGING_NOT_ALLOWED') {
        alert('Messaging is only available on the Global Enterprise plan.');
      }
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── File selection ────────────────────────────────────────────────────
  const handleFileSelect = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setShowAttachMenu(false);
    e.target.value = '';

    // Image: show preview first
    if (type === 'image' && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview({ file, url: ev.target.result, name: file.name });
      reader.readAsDataURL(file);
      return;
    }

    // Non-image file: upload immediately
    await uploadFile(file);
  };

  const uploadFile = async (file, caption = '') => {
    setUploading(true);
    setUploadProgress(10);
    try {
      // Simulate progress steps while actual upload runs
      const progressInterval = setInterval(() => {
        setUploadProgress(p => p < 85 ? p + 15 : p);
      }, 300);

      const sentMsg = await messageService.uploadAttachment(convId, user.id, file, caption);
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (sentMsg) {
        const msg = sentMsg.message || sentMsg;
        setMessages(prev => [...prev, { ...msg, isOwn: true }]);
      }
      messageService.getConversations(user.id).catch(() => {});
    } catch (err) {
      if (err.code === 'MESSAGING_NOT_ALLOWED') {
        alert('Messaging is only available on the Global Enterprise plan.');
      } else {
        alert('Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ── Send image with optional caption ─────────────────────────────────
  const handleSendImage = async () => {
    if (!imagePreview) return;
    const caption = inputText.trim();
    setImagePreview(null);
    setInputText('');
    await uploadFile(imagePreview.file, caption);
  };

  // ── Profile navigation ────────────────────────────────────────────────
  const handleProfileClick = () => {
    if (otherUser?.sellerSlug) {
      navigate(`/supplier/${otherUser.sellerSlug}`);
    } else if (otherUser?.sellerId) {
      navigate(`/supplier/${otherUser.sellerId}`);
    }
    // else: no profile to navigate to
  };

  const hasProfile = !!(otherUser?.sellerSlug || otherUser?.sellerId);

  const senderName = otherUser?.name || 'Chat';
  const senderAvatar = getAvatarUrl(otherUser?.avatar);
  const senderInitial = senderName.charAt(0).toUpperCase();

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="mc-page">
      {/* ── Header ── */}
      <div className="mc-header">
        <button className="mc-back-btn" onClick={() => navigate('/mobile/messenger')} aria-label="Back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          className={`mc-header-profile ${hasProfile ? 'mc-header-profile--clickable' : ''}`}
          onClick={hasProfile ? handleProfileClick : undefined}
          type="button"
        >
          <div className="mc-header-avatar">
            {senderAvatar
              ? <img src={senderAvatar} alt={senderName} />
              : <span>{senderInitial}</span>
            }
          </div>
          <div className="mc-header-info">
            <div className="mc-header-name">{senderName}</div>
            {hasProfile && <div className="mc-header-sub">View profile →</div>}
          </div>
        </button>
      </div>

      {/* ── Image preview overlay ── */}
      {imagePreview && (
        <div className="mc-img-preview-overlay">
          <div className="mc-img-preview-box">
            <div className="mc-img-preview-header">
              <span>{imagePreview.name}</span>
              <button onClick={() => setImagePreview(null)} className="mc-img-preview-close">✕</button>
            </div>
            <img src={imagePreview.url} alt="Preview" className="mc-img-preview-img" />
            <div className="mc-img-preview-footer">
              <input
                className="mc-img-caption-input"
                placeholder="Add a caption..."
                value={inputText}
                onChange={e => setInputText(e.target.value)}
              />
              <button
                className="mc-img-send-btn"
                onClick={handleSendImage}
                disabled={uploading}
                type="button"
              >
                {uploading ? '...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Messages ── */}
      <div className="mc-messages">
        {loading ? (
          <div className="mc-loading">
            {[1, 2, 3].map(i => (
              <div key={i} className={`mc-skel-row ${i % 2 === 0 ? 'mc-skel-right' : 'mc-skel-left'}`}>
                <div className="mc-skel-bubble" style={{ width: `${50 + i * 15}%` }} />
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="mc-empty"><p>No messages yet. Say hello!</p></div>
        ) : (
          messages.map((msg, idx) => {
            const isMine = msg.isOwn || msg.senderId === user?.id;
            const showDate = idx === 0 ||
              new Date(msg.createdAt).toDateString() !== new Date(messages[idx - 1]?.createdAt).toDateString();
            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className="mc-date-divider">
                    {new Date(msg.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                  </div>
                )}
                <div className={`mc-bubble-row ${isMine ? 'mc-mine' : 'mc-theirs'}`}>
                  <div className={`mc-bubble ${isMine ? 'mc-bubble-mine' : 'mc-bubble-theirs'}`}>

                    {/* ── Image attachment ── */}
                    {msg.type === 'image' && msg.attachmentUrl ? (
                      <div className="mc-attach-img-wrap">
                        <img
                          src={getAttachmentUrl(msg.attachmentUrl)}
                          alt="Shared"
                          className="mc-bubble-img"
                          onClick={() => window.open(getAttachmentUrl(msg.attachmentUrl), '_blank')}
                        />
                        {msg.content && msg.content !== msg.attachmentName && (
                          <p className="mc-img-caption">{msg.content}</p>
                        )}
                      </div>
                    ) : msg.type === 'file' && msg.attachmentUrl ? (
                      /* ── File attachment ── */
                      <a
                        href={getAttachmentUrl(msg.attachmentUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mc-file-card"
                      >
                        <div className="mc-file-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                        </div>
                        <div className="mc-file-info">
                          <span className="mc-file-name">{msg.attachmentName || msg.content || 'File'}</span>
                          {msg.attachmentSize > 0 && (
                            <span className="mc-file-size">{formatFileSize(msg.attachmentSize)}</span>
                          )}
                        </div>
                        <div className="mc-file-dl">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                        </div>
                      </a>
                    ) : (
                      /* ── Text message ── */
                      <span className="mc-bubble-text">{msg.content}</span>
                    )}

                    <span className="mc-bubble-time">
                      {formatTime(msg.createdAt)}
                      {isMine && (
                        <TickIcon status={
                          msg.status === 'sending' ? 'sending'
                          : (msg.isRead || msg.status === 'read') ? 'read'
                          : 'delivered'
                        } />
                      )}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}

        {/* Upload progress bar */}
        {uploading && (
          <div className="mc-upload-progress">
            <div className="mc-upload-bar" style={{ width: `${uploadProgress}%` }} />
            <span className="mc-upload-label">Uploading… {uploadProgress}%</span>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input bar ── */}
      <div className="mc-input-bar">
        {/* Attach button + popup menu */}
        <div className="mc-attach-wrap" ref={attachMenuRef}>
          <button
            className="mc-attach-btn"
            onClick={() => setShowAttachMenu(v => !v)}
            type="button"
            aria-label="Attach"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>

          {showAttachMenu && (
            <div className="mc-attach-menu">
              <button
                type="button"
                className="mc-attach-option"
                onClick={() => { imageInputRef.current?.click(); setShowAttachMenu(false); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                Image
              </button>
              <button
                type="button"
                className="mc-attach-option"
                onClick={() => { fileInputRef.current?.click(); setShowAttachMenu(false); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                File
              </button>
            </div>
          )}
        </div>

        {/* Hidden file inputs */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={e => handleFileSelect(e, 'image')}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
          style={{ display: 'none' }}
          onChange={e => handleFileSelect(e, 'file')}
        />

        <textarea
          ref={inputRef}
          className="mc-input"
          placeholder="Type a message..."
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <button
          className={`mc-send-btn ${inputText.trim() ? 'mc-send-active' : ''}`}
          onClick={handleSend}
          disabled={(!inputText.trim() && !imagePreview) || sending || uploading}
          type="button"
          aria-label="Send"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default MobileChatPage;

